import { useState } from "react"
import { ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import type { QuestionnaireItem } from "@/types/questionnaire-types"

// ===================
// Types
// ===================

interface QuestionBlockProps {
  question: QuestionnaireItem
}

// ===================
// Main Component
// ===================

export function QuestionBlock({ question }: QuestionBlockProps) {
  const [guidanceExpanded, setGuidanceExpanded] = useState(false)

  const hasMetadata =
    question.wordLimit != null ||
    question.weight != null ||
    question.mandatory ||
    !!question.guidance

  return (
    <div className="px-6 py-4 border-b border-border">
      {/* Question number + prompt */}
      <div className="flex items-baseline gap-2.5">
        <span className="text-xs font-mono text-muted-foreground shrink-0">
          {question.number}
        </span>
        <p className="text-sm font-medium leading-relaxed flex-1">
          {question.prompt}
        </p>
      </div>

      {/* Metadata — inline muted text, no chips */}
      {hasMetadata && (
        <div className="flex items-center gap-3 text-meta mt-2">
          {question.wordLimit != null && (
            <span>{question.wordLimit} word limit</span>
          )}
          {question.weight != null && (
            <span>Weight {question.weight}%</span>
          )}
          {question.mandatory && <span>Required</span>}

          {question.guidance && (
            <button
              onClick={() => setGuidanceExpanded(!guidanceExpanded)}
              className="flex items-center gap-1 hover:text-foreground transition-colors ml-auto"
            >
              <ChevronRight
                className={cn(
                  "h-3 w-3 transition-transform",
                  guidanceExpanded && "rotate-90"
                )}
              />
              Scoring guidance
            </button>
          )}
        </div>
      )}

      {/* Expanded guidance */}
      {question.guidance && guidanceExpanded && (
        <p className="text-meta mt-2 leading-relaxed whitespace-pre-wrap">
          {question.guidance}
        </p>
      )}
    </div>
  )
}
